/**
 * chatColorVarMap - Single source of truth for chat-colors key › CSS variable mapping
 *
 * Used by:
 *   - loader/helpers/injectChatColors.ts  (production widget)
 *   - app/src/pages/SpaceConfig/WebchatConfigPage.tsx  (design preview)
 *
 * Keys NOT present here are behavioural flags (show-layout-toggle, typing-speed,
 * disable-legal-banner, company-logo, back-button-variant, etc.) and should NOT
 * be injected as CSS variables.
 */

export const COLOR_VAR_MAP: Record<string, string> = {
  // Surface / backgrounds
  'surface':              '--chat-surface',
  'user-message':         '--chat-user-message',
  'user-message-text':    '--chat-user-message-text',
  'input':                '--chat-input',
  'hover':                '--chat-hover',

  // Primary
  'primary':              '--chat-primary',
  'primary-hover':        '--chat-primary-hover',
  'primary-border':       '--chat-primary-border',
  'primary-text':         '--chat-primary-text',

  // Buttons — default
  'button-default':       '--chat-button-default',
  'button-default-hover': '--chat-button-default-hover',
  'button-default-border':'--chat-button-default-border',
  'button-default-text':  '--chat-button-default-text',

  // Buttons — back
  'button-back':          '--chat-button-back',
  'button-back-hover':    '--chat-button-back-hover',
  'button-back-border':   '--chat-button-back-border',
  'button-back-text':     '--chat-button-back-text',

  // Buttons — dark
  'button-dark':          '--chat-button-dark',
  'button-dark-hover':    '--chat-button-dark-hover',
  'button-dark-border':   '--chat-button-dark-border',
  'button-dark-text':     '--chat-button-dark-text',

  // Buttons — gray
  'button-gray':          '--chat-button-gray',
  'button-gray-hover':    '--chat-button-gray-hover',
  'button-gray-border':   '--chat-button-gray-border',
  'button-gray-text':     '--chat-button-gray-text',

  // Buttons — subtle (brand-tinted secondary pills, used in NavigationRenderer grid)
  'btn-subtle-bg':        '--chat-btn-subtle-bg',
  'btn-subtle-hover':     '--chat-btn-subtle-hover',
  'btn-subtle-border':    '--chat-btn-subtle-border',
  'btn-subtle-text':      '--chat-btn-subtle-text',

  // Borders
  'border':               '--chat-border',
  'input-border':         '--chat-input-border',

  // Text
  'text':                 '--chat-text',
  'assistant-text':       '--chat-assistant-text',
  'placeholder':          '--chat-placeholder',
  'input-text':           '--chat-input-text',

  // Disabled
  'disabled':             '--chat-disabled',
  'disabled-text':        '--chat-disabled-text',

  // Loading / typing indicator
  'typing':               '--chat-typing',
  'loading':              '--chat-loading',

  // Workflow panel
  'workflow-text':           '--chat-workflow-text',
  'workflow-text-secondary': '--chat-workflow-text-secondary',
  'workflow-border':         '--chat-workflow-border',

  // Code / markdown
  'code':                 '--chat-code',
  'code-text':            '--chat-code-text',
  'link':                 '--chat-link',
  'link-hover':           '--chat-link-hover',

  // Shadows
  // input-shadow, input-shadow-hover, input-shadow-focus are intentionally NOT mapped here.
  // They are always derived from --chat-input via CSS (oklch relative color) so they
  // auto-adapt to any theme without ever being hardcoded in the database.
  'shadow':               '--chat-shadow',
  'button-shadow':        '--chat-button-shadow',

  // Button geometry
  'button-radius':        '--chat-button-radius',
  'button-padding':       '--chat-button-padding',

  // Assistant response container
  'assistant-response-radius':       '--chat-assistant-response-radius',
  'back-button-variant':             '--chat-back-button-variant',
  'assistant-response-border':       '--chat-assistant-response-border',
  'assistant-response-border-width': '--chat-assistant-response-border-width',
  'assistant-response-background':   '--chat-assistant-response-background',
  'assistant-response-padding':      '--chat-assistant-response-padding',

  // Typography
  'greeting-font-weight': '--chat-greeting-font-weight',
  'title-font-family':    '--chat-title-font-family',
  'title-inline':         '--chat-title-inline',
  'home-show-titles':     '--chat-home-show-titles',
  'font-size':            '--chat-font-size',
  'line-height':          '--chat-line-height',

  // Input variant
  'input-variant':        '--chat-input-variant',

  // Overlay / popup
  'overlay-background':   '--chat-overlay-background',
  'popup-radius':         '--chat-popup-radius',
  'popup-width':          '--chat-popup-width',
  'popup-height':         '--chat-popup-height',

  // Header buttons
  'header-button-background': '--chat-header-button-background',
  'header-button-hover':      '--chat-header-button-hover',
  'header-button-icon':       '--chat-header-button-icon',

  // Input button (send)
  'input-button-background':     '--chat-input-button-background',
  'input-button-hover':          '--chat-input-button-hover',
  'input-button-icon':           '--chat-input-button-icon',
  'input-button-disabled':       '--chat-input-button-disabled',
  'input-button-disabled-icon':  '--chat-input-button-disabled-icon',

  // Radios por elemento (2026-06-13) — el cliente puede redondear cada pieza
  'bubble-radius':        '--chat-bubble-radius',
  'input-radius':         '--chat-input-radius',
  'block-radius':         '--chat-block-radius',
  'media-radius':         '--chat-media-radius',
  'field-radius':         '--chat-field-radius',
  'control-radius':       '--chat-control-radius',
  'popover-radius':       '--chat-popover-radius',
  'avatar-radius':        '--chat-avatar-radius',

  // Launcher / MoFace
  'launcher-size':        '--chat-launcher-size',
  'launcher-color':       '--chat-launcher-color',
  'launcher-border':      '--chat-launcher-border',

  // Header
  'header-height':        '--chat-header-height',

  // Tipografía
  'title-size':           '--chat-title-size',
  'heading-weight':       '--chat-heading-weight',

  // Espaciado / scrims
  'scrim':                '--chat-scrim',
};

/**
 * Transform special values before injecting as CSS.
 * This must be applied in both the loader and the preview.
 */
export function transformColorValue( key: string, value: string ): string {
  // title-font-family: expand shorthand names to full font stacks
  if( key === 'title-font-family' ) {
    if( value.toLowerCase() === 'inter' )
      return "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', sans-serif";
    if( value.toLowerCase() === 'hedvig' )
      return "'Hedvig Letters Serif', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', serif";
  }

  // overlay-background: migrate old hardcoded gradient to dynamic color-mix
  if( key === 'overlay-background' && value.includes( 'linear-gradient' ) ) {
    return 'color-mix(in srgb, var(--chat-surface) 85%, var(--chat-primary) 15%)';
  }

  return value;
}

/**
 * Sanitize a CSS value to prevent CSS injection attacks.
 * Strips characters that could break out of a CSS declaration or inject
 * new rules: ; { } ( ) < > ' " \
 *
 * SECURITY: This MUST be applied to all user-configurable values before
 * they are interpolated into CSS strings (style tags, inline styles, etc.).
 */
export function sanitizeCSSValue( val: string ): string {
  // Remove characters that could break out of a CSS value context
  return val.replace( /[;{}<>'"\\]/g, '' );
}

/**
 * Normalize persisted chat-colors values before CSS injection.
 * Accepts strings, numbers and booleans; ignores arrays/objects/null.
 */
export function normalizeChatColorValue( value: unknown ): string | null {
  if( typeof value === 'string' ) {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  if( typeof value === 'number' ) {
    if( !Number.isFinite( value ) ) return null;
    return String( value );
  }

  if( typeof value === 'boolean' ) {
    return value ? 'true' : 'false';
  }

  return null;
}

/**
 * Normalize an entire chat-colors object, discarding invalid values.
 */
export function normalizeChatColorsConfig( colors: unknown ): Record<string, string> {
  if( !colors || typeof colors !== 'object' || Array.isArray( colors ) )
    return {};

  const normalized: Record<string, string> = {};
  Object.entries( colors as Record<string, unknown> ).forEach( ( [key, rawValue] ) => {
    const value = normalizeChatColorValue( rawValue );
    if( value !== null )
      normalized[key] = value;
  } );

  return normalized;
}
